/* eslint-disable react/no-array-index-key */
import {css, useMediaQuery, useTheme, Typography} from '@mui/material';
import {
  ConfigObjectType,
  GetStylesFunction,
  IconHeaderWithSearch,
  mixins,
  omitNullsy,
  LightSplitLayout,
  useLocationQuery,
  LightSplitLayoutProps,
  useMdOrSmaller,
  enumContainer,
  LoadingSpinner,
  ArrayUtils,
  asQueryString,
  ExportUtils,
  useCurrentLocale,
  ConfirmDialog,
  DlMuiDynamicIcon,
  useApiQuery,
} from '@wallaby-ui/core';
import {
  JustTooltipFormatter,
  AG_GRID_COMPATIBILITY,
  useTableData,
  MaterialTableWrapper,
  getDefaultMTOptions,
  DEFAULT_PAGE_SIZE,
  useFormatters,
  Formatters,
  HaidiSchema,
  LightSelectedFilters,
  LightSelectedFiltersPane,
  useTableParams,
  clampPageNumber,
  useSnomedLabels,
  HaidiAtbSusceptibilityListFormatter,
  LightTextFilter,
  LightSelectFilter,
  LightDateRangeFilter,
  useOrganizationsByOrgSecurity,
  WallabyCoreSchema,
  useFinding,
  UserFilterDto,
  TableSorting,
  useUserFilters,
  TablePaging,
  LightFilterPanel,
  LightOrganizationFilter2,
  ShortcutEnum,
} from '@wallaby-ui/document';
import {isEqual, sortBy} from 'lodash';
import {Column} from 'material-table';
import * as React from 'react';
import {useCallback, useMemo, useRef, useState} from 'react';
import {Helmet} from 'react-helmet';

import {useAppSelector} from 'src/js/store_hooks';

const DEFAULT_LABS_FILTERS_OPEN = true;
const DEFAULT_LABS_FILTERS: Partial<LabsFilters> = {
  measureType: 'ALL_ISOLATES',
};
const DEFAULT_LABS_SORTING = {
  field: 'samplingDate',
  direction: 'desc',
};
const DEFAULT_LABS_PAGING = {};
type LabsFilters = {
  patientFullName?: string;
  patientIdentificationNumber?: string;
  samplingDateFrom?: string;
  samplingDateTo?: string;
  samplingFloatingShortcutName?: ShortcutEnum | null;
  findingSnomedCode?: string[];
  measureType?: string;
  specimenTypes?: string[];
  hasAtbSusceptibility?: boolean;
  organizationIds?: string[];
  patientSrcId?: string;
};
type FiltersData = {
  labsOpen: boolean;
  labs: LabsFilters;
  labsSorting: TableSorting;
  labsPaging: TablePaging;
};

type RowType = HaidiSchema.components['schemas']['LabResultListDto'];
function useColumns(
  formatters: Formatters,
  defaultSort: TableSorting,
  snomedLabels: Record<string, string>,
  snomedLabelsLoading: boolean,
  muiStyles: any,
  filters: LabsFilters
): Column<RowType>[] {
  const {t, tEnum, formatDate} = formatters;
  const mdOrSmaller = useMdOrSmaller();
  const columns: Column<RowType>[] = [
    {
      field: 'patientFullName',
      title: t('labs.patientFullName'),
      render: (data) => (
        <JustTooltipFormatter
          {...AG_GRID_COMPATIBILITY}
          icon={{iconName: 'haidiLabIcon', useFixedViewBox: '0 0 12 13'}}
          value={data?.patientFullName}
          route={
            (data.encounterId ? `/encounter/${data.encounterId}` : '') +
            (data.documentVersionId ? `/document/${data.documentVersionId}` : '')
          }
        />
      ),
      width: '188px', // NOTE: 23px icon + 165px text
      sorting: true,
    },
    {
      field: 'patientIdentificationNumber',
      title: t('labs.patientIdentificationNumber'),
      tooltip: t('labs.patientIdentificationNumber.tooltip'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={data?.patientIdentificationNumber} />,
      // width: '162px', // single line
      width: '110px', // two lines
    },
    {
      field: 'samplingDate',
      title: t('labs.samplingDate'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={formatDate(data?.samplingDate ?? '')} />,
      width: '96px',
      sorting: true,
    },
    {
      field: 'samplingOrganization',
      title: t('labs.samplingOrganization'),
      tooltip: t('labs.samplingOrganization.tooltip'),
      render: (data) => (
        <JustTooltipFormatter
          {...AG_GRID_COMPATIBILITY}
          value={data?.samplingOrganizationShortName}
          tooltipOverride={data?.samplingOrganizationFullLabel}
          singleLine
        />
      ),
      width: '100px',
    },
    {
      field: 'specimenType',
      title: t('labs.specimenType'),
      render: (data) => (
        <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={tEnum('LabReportSpecimenTypeEnum', data?.specimenType)} singleLine />
      ),
      width: '119px',
    },
    {
      field: 'specimenSnippet',
      title: t('labs.specimenSnippet'),
      render: (data) => <JustTooltipFormatter {...AG_GRID_COMPATIBILITY} value={data?.specimenSnippet} />,
    },
    {
      field: 'findingSnomedCode',
      title: t('labs.findingSnomedCode'),
      tooltip: t('labs.findingSnomedCode.tooltip'),
      render: (data) => (
        <JustTooltipFormatter
          {...AG_GRID_COMPATIBILITY}
          value={snomedLabels[data?.findingSnomedCode ?? ''] || (snomedLabelsLoading ? '' : data?.findingSnomedCode)}
          route={
            data?.findingSnomedCode
              ? `/pathogenDetail?${asQueryString({
                  pathogen: {
                    dateFrom: filters.samplingDateFrom,
                    dateTo: filters.samplingDateTo,
                    specimenTypes: filters.specimenTypes,
                    pathogenIds: [data.findingSnomedCode],
                    organizationIds: filters.organizationIds,
                  },
                })}`
              : ''
          }
        />
      ),
      width: mdOrSmaller ? '176px' : undefined,
    },
    {
      field: 'atbSusceptibilities',
      title: t('labs.atbSusceptibilities'),
      render: (data) => (
        <div css={muiStyles.atbSusceptibilitiesWrapper}>
          <HaidiAtbSusceptibilityListFormatter
            value={(data.atbSusceptibilities ?? []).map((v) => ({
              atbSusceptibility: v.sus as HaidiSchema.components['schemas']['LabAtbSusceptibilityInterpretationCodeEnum'],
              atbSnomedCode: v.atb,
            }))}
            snomedLabels={snomedLabels}
            snomedLabelsLoading={snomedLabelsLoading}
          />
        </div>
      ),
    },
  ];
  return columns.map((v) => ({
    ...v,
    sorting: v.sorting ?? false,
    customSort: v.customSort ?? (() => 0),
    ...(v.field === defaultSort.field ? {defaultSort: defaultSort.direction ?? undefined} : {}),
  }));
}

interface OwnProps {
  config: ConfigObjectType;
  route: string;
}
type Props = OwnProps;
export function normalizeLabsFiltersForCompare(filter: Record<string, any>): Record<string, any> {
  const copy: Record<string, any> = structuredClone(filter ?? {});
  if (copy.samplingFloatingShortcutName) {
    delete (copy as any).samplingDateFrom;
    delete (copy as any).samplingDateTo;
  }
  return copy;
}
export const LabResultsListContainer: React.FC<Props> = (props) => {
  const {config} = props;
  const component = 'labReport';
  const storageIdPrefix = '/labs';
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true);
  const filters: FiltersData = useMemo(() => {
    return {
      labsOpen: query?.labsOpen ?? DEFAULT_LABS_FILTERS_OPEN,
      labs: (query?.labs ?? DEFAULT_LABS_FILTERS) as LabsFilters,
      labsSorting: query?.labsSorting ?? DEFAULT_LABS_SORTING,
      labsPaging: query?.labsPaging ?? DEFAULT_LABS_PAGING,
    };
  }, [query]);
  const {waitForUserFilters, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter} = useUserFilters({
    storageIdPrefix,
    component,
    config,
    changeFilters: (newFilters) => changeFilters({labs: newFilters}),
  });
  if (waitForUserFilters) return <LoadingSpinner isVisible marginTop={16} />;
  return (
    <LabResultsListContainerInner
      {...props}
      storageIdPrefix={storageIdPrefix}
      component={component}
      userFiltersLoading={userFiltersLoading}
      addedUserFilters={addedUserFilters}
      userFilters={userFilters}
      changeUserFilter={changeUserFilter}
      filters={filters}
      changeFilters={changeFilters}
    />
  );
};
LabResultsListContainer.displayName = 'LabResultsListContainer';

type InnerProps = Props & {
  storageIdPrefix: string;
  component: string;
  userFiltersLoading: boolean;
  addedUserFilters: UserFilterDto[];
  userFilters: UserFilterDto[];
  changeUserFilter: (newUserFilter: UserFilterDto, toDelete?: boolean) => void;
  filters: FiltersData;
  changeFilters: (filterDiff: Record<string, any>) => void;
};
function useFiltersInitSize() {
  const smallerThan1280 = useMediaQuery('(max-width:1280px)', {noSsr: true});
  const smallerThan1440 = useMediaQuery('(max-width:1440px)', {noSsr: true});
  if (smallerThan1280) return [282, 400];
  if (smallerThan1440) return [269, 414];
  return [127, 556];
}
const LabResultsListContainerInner: React.FC<InnerProps> = (props) => {
  const {config, storageIdPrefix, component, userFiltersLoading, addedUserFilters, userFilters, changeUserFilter, filters, changeFilters} =
    props;
  const theme = useTheme();
  const muiStyles = getStyles({theme});

  const labsListUrl = config.service.url.labsList;
  const labsListParams = useTableParams(
    {
      page: filters.labsPaging.page ?? 0,
      size: filters.labsPaging.pageSize ?? DEFAULT_PAGE_SIZE,
      patientFullName: filters.labs.patientFullName,
      patientIdentificationNumber: filters.labs.patientIdentificationNumber,
      samplingDateFrom: filters.labs.samplingDateFrom,
      samplingDateTo: filters.labs.samplingDateTo,
      samplingFloatingShortcutName: filters.labs.samplingFloatingShortcutName,
      pathogenIds: filters.labs.findingSnomedCode,
      measureType: filters.labs.measureType,
      specimenTypes: filters.labs.specimenTypes,
      hasAtbSusceptibility: filters.labs.hasAtbSusceptibility,
      organizationIds: filters.labs.organizationIds,
      patientSrcId: filters.labs.patientSrcId,
    },
    filters.labsSorting,
    {
      patientFullName: ['patient.lastName', 'patient.firstName'],
    }
  );
  const {data, isLoading, page, countOnPage, totalCount, totalPages} = useTableData<any, RowType[]>(labsListUrl, labsListParams);

  const formatters = useFormatters();
  const {t, tEnum, formatDate} = formatters;
  const enumMap = useAppSelector((state) => (state[enumContainer.ENUM_REDUCER_STATE_ID] as any).enumMap);
  const sortByMessageKey = useCallback((options) => sortBy(options, [(v) => t(v.messageKey)]), [t]);
  const hasFilters = !isEqual(omitNullsy(filters.labs), omitNullsy(DEFAULT_LABS_FILTERS));
  const hasSorting = !isEqual(omitNullsy(filters.labsSorting), omitNullsy(DEFAULT_LABS_SORTING));
  const {snomedLabels, snomedLabelsLoading} = useSnomedLabels(config);
  const agens = useFinding(config);
  const findingOptions = useMemo(() => {
    return sortBy(
      agens.map((v) => ({id: v.code, label: v.label})),
      (v) => v.label
    );
  }, [agens]);
  type Organization = WallabyCoreSchema.components['schemas']['OrganizationFilterDto'];
  const {data: organizations} = useOrganizationsByOrgSecurity<Organization>(config);
  const columns = useColumns(formatters, filters.labsSorting, snomedLabels, snomedLabelsLoading, muiStyles, filters.labs);

  const apiUrl = config.service.url.maxExportRows;

  const {data: maxExportedRows} = useApiQuery<number | undefined>([apiUrl], null, {
    axiosConfig: {
      url: apiUrl,
    },
    reactQueryOptions: {
      enabled: true,
    },
  });

  // Bottom-most filter-inputs row; measured by autoFitFilterRows to default the divider to 2 rows + a peek.
  const filterInputsRef = useRef<HTMLDivElement>(null);
  const splitA: LightSplitLayoutProps = {
    initSizes: useFiltersInitSize(),
    minSizes: [64, 112],
    storageId: '/SplitLayout.LabsList.A',
    isVertical: true,
    noGrow: [!filters.labsOpen, false],
    hidden: [false, false],
    autoFitFilterRows: {contentRef: filterInputsRef, enabled: filters.labsOpen},
  };
  const atbSusceptibilityOptions: enumContainer.EnumDefinition[] = [
    {name: 'yes', messageKey: 'labs.hasAtbSusceptibility.yes'},
    {name: 'no', messageKey: 'labs.hasAtbSusceptibility.no'},
  ];
  const currentLocale = useCurrentLocale();

  const downloadXlsxExport = useCallback(() => {
    ExportUtils.handleExportXlsx({
      url: config.service.url.labsListXlsxExport,
      filters: {
        patientSrcId: filters.labs.patientSrcId,
        patientFullName: filters.labs.patientFullName,
        patientIdentificationNumber: filters.labs.patientIdentificationNumber,
        samplingDateFrom: filters.labs.samplingDateFrom,
        samplingDateTo: filters.labs.samplingDateTo,
        organizationIds: filters.labs.organizationIds,
        specimenTypes: filters.labs.specimenTypes,
        hasAtbSusceptibility: filters.labs.hasAtbSusceptibility,
        pathogenIds: filters.labs.findingSnomedCode,
        measureType: filters.labs.measureType,
      },
      locale: currentLocale,
    });
  }, [config.service.url.labsListXlsxExport, currentLocale, filters.labs]);
  const [confirmExportWarningOpened, setConfirmExportWarningOpened] = useState(false);
  const onConfirmExportWarning = useCallback(() => {
    setConfirmExportWarningOpened(false);
    downloadXlsxExport();
  }, [downloadXlsxExport]);
  const handleDialogCancel = () => {
    // do nothing
  };

  const tableFiltersForSelectedPane = useMemo(() => {
    const hasSamplingDate = !!filters.labs.samplingDateFrom && !!filters.labs.samplingDateTo;

    const result: any = {
      ...filters.labs,
    };

    if (hasSamplingDate) {
      result.samplingDate = {
        startDate: filters.labs.samplingDateFrom,
        endDate: filters.labs.samplingDateTo,
        shortcut: filters.labs.samplingFloatingShortcutName,
      };
    } else {
      delete result.samplingDate;
    }

    return result;
  }, [filters.labs]);

  const selectedFiltersPane =
    Object.keys(omitNullsy(tableFiltersForSelectedPane)).length > 0 ? (
      <LightSelectedFiltersPane
        selectedFilters={[
          {
            name: 'patientFullName',
            label: t('labs.patientFullName'),
            value: filters.labs.patientFullName,
          },
          {
            name: 'patientIdentificationNumber',
            label: t('labs.patientIdentificationNumber'),
            value: filters.labs.patientIdentificationNumber,
          },
          {
            name: ['samplingDateFrom', 'samplingDateTo'],
            label: t('labs.samplingDate'),
            value: `${formatDate(filters.labs.samplingDateFrom ?? '')} - ${formatDate(filters.labs.samplingDateTo ?? '')}`,
          },
          {
            name: 'findingSnomedCode',
            label: t('filter.pathogen'),
            value: (filters.labs.findingSnomedCode ?? []).map((v) => snomedLabels[v]).join(', '),
          },
          {
            name: 'specimenTypes',
            label: t('labs.specimenType'),
            value: (filters.labs.specimenTypes ?? []).map((v) => tEnum('LabReportSpecimenTypeEnum', v)).join(', '),
          },
          {
            name: 'hasAtbSusceptibility',
            label: t('labs.hasAtbSusceptibility'),
            value: t(`labs.hasAtbSusceptibility.${filters.labs.hasAtbSusceptibility ? 'yes' : 'no'}`),
          },
          {
            name: 'measureType',
            label: t('filter.antibiogram.measureType'),
            value: tEnum('AntStewardshipMeasureType', filters.labs.measureType),
            defaultValue: DEFAULT_LABS_FILTERS.measureType,
          },
          {
            name: 'organizationIds',
            label: t('filter.organizationIds'),
            value: (filters.labs.organizationIds ?? [])
              .map((id) => organizations.find((w) => String(w.id) === String(id))?.organizationLongName)
              .join(', '),
          },
          {
            name: 'patientSrcId',
            label: t('filter.patient.srcId'),
            value: filters.labs.patientSrcId,
          },
        ]}
        tableFilters={tableFiltersForSelectedPane}
        changeTableFilters={(newTableFilters) =>
          changeFilters({
            labs: newTableFilters,
            labsPaging: {
              ...filters.labsPaging,
              page: 0,
            },
          })
        }
        formatters={formatters}
      />
    ) : null;
  return (
    <div css={muiStyles.contentWrapper}>
      <Helmet title={`${t('navigation.section.atbReports.browser')} | ${t('navigation.route.atbReports.labList')}`} />
      <LightSplitLayout {...splitA}>
        <div css={muiStyles.splitContentBox}>
          <LightFilterPanel
            open={filters.labsOpen}
            toggleOpen={() =>
              changeFilters({
                labsOpen: !filters.labsOpen,
              })
            }
            hasActiveFilter={hasFilters}
            filterInputsRef={filterInputsRef}
            savedFiltersProps={{
              storageIdPrefix,
              component,
              userFiltersLoading,
              addedUserFilters,
              userFilters,
              changeUserFilter,
              filter: filters.labs,
              setFilter: (newFilter) => {
                changeFilters({
                  labs: newFilter,
                });
              },
              config,
              normalizeForCompare: normalizeLabsFiltersForCompare,
            }}
            selectedFilters={selectedFiltersPane}
          >
            <LightTextFilter
              config={{
                name: 'patientFullName',
                ui: {label: 'labs.patientFullName'},
              }}
              value={filters.labs.patientFullName}
              onChange={(newValue) =>
                changeFilters({
                  labs: {
                    ...filters.labs,
                    patientFullName: newValue,
                  },
                  labsPaging: {
                    ...filters.labsPaging,
                    page: 0,
                  },
                })
              }
            />
            <LightTextFilter
              config={{
                name: 'patientIdentificationNumber',
                ui: {label: 'labs.patientIdentificationNumber'},
              }}
              value={filters.labs.patientIdentificationNumber}
              onChange={(newValue) =>
                changeFilters({
                  labs: {
                    ...filters.labs,
                    patientIdentificationNumber: newValue,
                  },
                  labsPaging: {
                    ...filters.labsPaging,
                    page: 0,
                  },
                })
              }
            />
            <LightDateRangeFilter
              config={{
                name: 'samplingDate',
                ui: {label: 'labs.samplingDate'},
              }}
              value={
                filters.labs.samplingDateFrom && filters.labs.samplingDateTo
                  ? {
                      startDate: filters.labs.samplingDateFrom,
                      endDate: filters.labs.samplingDateTo,
                      shortcut: filters.labs.samplingFloatingShortcutName,
                    }
                  : null
              }
              onChange={(newValue) => {
                changeFilters({
                  labs: {
                    ...filters.labs,
                    samplingDateFrom: newValue?.startDate,
                    samplingDateTo: newValue?.endDate,
                    samplingFloatingShortcutName: newValue?.shortcut,
                  },
                  labsPaging: {
                    ...filters.labsPaging,
                    page: 0,
                  },
                });
              }}
              dateOnly
              showShortcuts
            />
            <LightSelectFilter
              config={{
                name: `findingSnomedCode`,
                ui: {label: 'filter.pathogen'},
              }}
              options={findingOptions}
              multi
              value={filters.labs.findingSnomedCode}
              onChange={(newValue) => {
                changeFilters({
                  labs: {
                    ...filters.labs,
                    findingSnomedCode: newValue,
                  },
                  labsPaging: {
                    ...filters.labsPaging,
                    page: 0,
                  },
                });
              }}
              /* filterOptions={(options, state) => {
              const {inputValue, getOptionLabel} = state;
              if (inputValue.length < 3) return [];
              return sortBy(
                options.filter((v) =>
                  getOptionLabel(v)
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                ),
                (v) => getOptionLabel(v).length
              );
            }} */
            />
            <LightSelectFilter
              config={{
                name: `specimenTypes`,
                ui: {label: 'labs.specimenType'},
              }}
              options={sortByMessageKey(enumMap.LabReportSpecimenTypeEnum.filter((a) => a.name !== 'UNKNOWN'))}
              multi
              value={filters.labs.specimenTypes}
              onChange={(newValue) => {
                changeFilters({
                  labs: {
                    ...filters.labs,
                    specimenTypes: newValue,
                  },
                  labsPaging: {
                    ...filters.labsPaging,
                    page: 0,
                  },
                });
              }}
            />
            <LightSelectFilter
              config={{
                name: `hasAtbSusceptibility`,
                ui: {label: 'labs.hasAtbSusceptibility'},
              }}
              options={atbSusceptibilityOptions}
              value={ArrayUtils.mapSingleBooleanToEnum(filters.labs.hasAtbSusceptibility, atbSusceptibilityOptions)}
              onChange={(newValue) => {
                changeFilters({
                  labs: {
                    ...filters.labs,
                    hasAtbSusceptibility: ArrayUtils.mapSingleEnumToBoolean(newValue, atbSusceptibilityOptions),
                  },
                  labsPaging: {
                    ...filters.labsPaging,
                    page: 0,
                  },
                });
              }}
            />
            <LightSelectFilter
              config={{
                name: `measureType`,
                ui: {label: 'filter.antibiogram.measureType'},
              }}
              options={sortByMessageKey(enumMap.AntStewardshipMeasureType)}
              value={filters.labs.measureType}
              onChange={(newValue) => {
                changeFilters({
                  labs: {
                    ...filters.labs,
                    measureType: newValue,
                  },
                  labsPaging: {
                    ...filters.labsPaging,
                    page: 0,
                  },
                });
              }}
              isClearable={false}
            />
            <LightOrganizationFilter2
              appConfig={config}
              config={{
                name: 'organizationIds',
                ui: {label: 'filter.organizationIds'},
              }}
              multi
              value={filters.labs.organizationIds}
              onChange={(newOrganizationIds) => {
                changeFilters({
                  labs: {
                    ...filters.labs,
                    organizationIds: newOrganizationIds,
                  },
                  labsPaging: {
                    ...filters.labsPaging,
                    page: 0,
                  },
                });
              }}
            />
            <LightTextFilter
              config={{
                name: 'patientSrcId',
                ui: {label: 'filter.patient.srcId'},
              }}
              value={filters.labs.patientSrcId}
              onChange={(newValue) =>
                changeFilters({
                  labs: {
                    ...filters.labs,
                    patientSrcId: newValue,
                  },
                  labsPaging: {
                    ...filters.labsPaging,
                    page: 0,
                  },
                })
              }
            />
          </LightFilterPanel>
        </div>
        <div data-name='labs-list-wrapper' css={muiStyles.contentBox}>
          <IconHeaderWithSearch
            iconName={'listIconMultiColor'}
            label={'labs.table'}
            useFixedViewBox='0 -1 16 14'
            iconCss={muiStyles.headerIcon}
          >
            <LightSelectedFilters
              hasSorting={hasSorting}
              handleResetSorting={() =>
                changeFilters({
                  labsSorting: DEFAULT_LABS_SORTING,
                  labsPaging: {
                    ...filters.labsPaging,
                    page: 0,
                  },
                })
              }
              hasFilters={hasFilters}
              handleResetFilters={() =>
                changeFilters({
                  labs: DEFAULT_LABS_FILTERS,
                  labsPaging: {
                    ...filters.labsPaging,
                    page: 0,
                  },
                })
              }
              hasXlsxExport
              handleXlsxExport={() => {
                if (!maxExportedRows) return;
                if (totalCount != null ? totalCount > maxExportedRows : false) {
                  setConfirmExportWarningOpened(true);
                } else {
                  downloadXlsxExport();
                }
              }}
            />
          </IconHeaderWithSearch>
          <ConfirmDialog
            isOpened={confirmExportWarningOpened}
            handleConfirm={onConfirmExportWarning}
            handleCancel={handleDialogCancel}
            showCancel={false}
            titleIcon={
              <DlMuiDynamicIcon
                iconName='haidiWarningIcon'
                useFixedViewBox='0 0 13 12'
                css={muiStyles.confirmIconButton}
                svgIconInputCss={muiStyles.confirmIcon}
                noButtonStyles
              />
            }
            titleText={t('common.export.confirmDialogLimit.title')}
            confirmText={t('common.confirmDialog.accept')}
          >
            <Typography variant='value'>{t('common.export.confirmDialogLimit.text')}</Typography>
          </ConfirmDialog>
          <MaterialTableWrapper
            options={{
              ...getDefaultMTOptions(countOnPage ?? DEFAULT_PAGE_SIZE),
            }}
            variant='big'
            useMaxHeight
            columns={columns}
            data={data ?? []}
            isLoading={isLoading || snomedLabelsLoading}
            totalCount={totalCount}
            page={page}
            totalPages={totalPages}
            onChangePage={(page, pageSize) =>
              changeFilters({
                labsPaging: {
                  ...filters.labsPaging,
                  page,
                  pageSize,
                },
              })
            }
            onChangeRowsPerPage={(pageSize) =>
              changeFilters({
                labsPaging: {
                  ...filters.labsPaging,
                  pageSize,
                  page: clampPageNumber(filters.labsPaging.page, pageSize, totalCount),
                },
              })
            }
            controlledPageSize={filters.labsPaging.pageSize ?? DEFAULT_PAGE_SIZE}
            onOrderChange={(colIndex, direction) => {
              const columnId = columns[colIndex].field as string;
              changeFilters({
                labsSorting: {
                  field: columnId,
                  direction,
                },
                labsPaging: {
                  ...filters.labsPaging,
                  page: 0,
                },
              });
            }}
            controlledSort={(() => {
              return filters.labsSorting;
            })()}
            scrollStorageId='/labs.scrollXY'
          />
        </div>
      </LightSplitLayout>
    </div>
  );
};

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
    padding: 16px 16px 16px 0;
  `,
  splitContentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px 16px 0 16px;
    row-gap: 16px;
  `,
  contentBox: css`
    ${mixins.contentBox};
    border-radius: ${options?.theme?.borderRadius};
    padding: ${options?.theme?.spacing(3)};
    background: #ffffff;
    padding: 16px;
    row-gap: 16px;
    overflow-y: auto;
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
  atbSusceptibilitiesWrapper: css`
    position: relative;
    height: 100%;
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    text-align: left;
  `,
});
